/**
 * QrLoginClaimPage - Mobile scan target for QR login.
 * User opens /auth/qr-login?code=... (from scanning desktop QR). If logged in,
 * claims the code via HTTP endpoint; desktop then signs in via custom token.
 */

import React, { useState, useEffect } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, Link, CircularProgress } from "@mui/material";
import { useAuth } from "@healthdesk/shared-hooks";
import { firebaseApp } from "@healthdesk/shared-services";

function getClaimUrl() {
  const projectId =
    firebaseApp.options?.projectId || (typeof process !== "undefined" && process.env?.REACT_APP_FIREBASE_PROJECT_ID);
  return projectId ? `https://us-central1-${projectId}.cloudfunctions.net/claimQrLoginCodeHttp` : null;
}

function friendlyClaimError(msg) {
  const m = (msg || "").trim();
  if (!m || m === "INTERNAL" || m.toUpperCase() === "INTERNAL") {
    return "Something went wrong. Please try again or sign in on the desktop with your phone number.";
  }
  // Network/load errors (e.g. Safari "Load failed", Chrome "Failed to fetch") — don't show raw to user.
  const networkLike = /^(load\s+failed|failed\s+to\s+fetch|failed\s+to\s+load|network\s+error)/i.test(m);
  if (networkLike) {
    return "Connection problem. Check your connection and try again, or sign in on the desktop with your phone number.";
  }
  return m;
}

function friendlyClaimErrorByStatus(status, message) {
  if (status === 401) {
    return "Your phone sign-in expired. Please sign in on your phone again, then scan the desktop QR again.";
  }
  if (status === 403) {
    return "You do not have permission to complete this login. Please contact support if this continues.";
  }
  if (status === 409) {
    return "This QR code was already used. Please generate a new QR code on the desktop.";
  }
  if (status === 404 || status === 410) {
    return "This QR code expired or is no longer valid. Please generate a new QR code on the desktop.";
  }
  return friendlyClaimError(message);
}

const QrLoginClaimPage = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const { user, userLoading } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | claiming | success | error
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!code || !user || status !== "idle") return;

    setStatus("claiming");
    setErrorMessage(null);

    const url = getClaimUrl();
    if (!url) {
      setStatus("error");
      setErrorMessage("Configuration error. Please try again.");
      return;
    }

    user
      .getIdToken(true)
      .then((idToken) =>
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, idToken }),
          cache: "no-store",
        })
      )
      .then((res) => {
        if (res.ok) {
          setStatus("success");
          return;
        }
        return res
          .json()
          .catch(() => ({}))
          .then((body) => {
            const err = new Error(body?.error || res.statusText || "Claim failed");
            err.status = res.status;
            throw err;
          });
      })
      .catch((e) => {
        setStatus("error");
        setErrorMessage(friendlyClaimErrorByStatus(e?.status, e?.message));
        console.warn("[QrLoginClaimPage] claim failed", e);
      });
  }, [code, user, status]);

  if (userLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
          Please log in on your phone first
        </Typography>
        <Link component={RouterLink} to="/auth">
          Go to sign in
        </Link>
      </Box>
    );
  }

  if (!code) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Invalid link. Scan the QR code on the desktop again.
        </Typography>
      </Box>
    );
  }

  if (status === "claiming") {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Logging you in on the desktop…
        </Typography>
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
        <Typography variant="body1" color="error" sx={{ mb: 2, textAlign: "center" }}>
          {errorMessage}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
          You can scan the QR on the desktop again, or sign in on the desktop with your phone number.
        </Typography>
      </Box>
    );
  }

  // Success: claim succeeded. The *desktop* browser will sign in and redirect to the dashboard; phone just sees this.
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Typography variant="h6" sx={{ textAlign: "center" }}>
        Success
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
        The desktop should sign in and open the app shortly. You can close this tab.
      </Typography>
    </Box>
  );
};

export default QrLoginClaimPage;
